import { Checkbox } from '@/components/ui/checkbox';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SelectField } from '@/components/ui/select-field';
import { SelectSearch } from '@/components/features/shared/SelectSearch';
import { CreateClientDto } from '@/domain/clients/client-schemas';
import { useClient, useClients } from '@/hooks/api/useClients';
import { UserStatus } from '@/domain/auth/auth';
import { IvaCondition } from '@/domain/invoices/invoice';
import { DocType, getDocTypeOptions } from '@/domain/shared';
import { useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';

interface InvoiceStepProps {
  form: UseFormReturn<CreateClientDto>;
  isPF: boolean;
}

export const InvoiceStep = ({ form, isPF }: InvoiceStepProps) => {
  const [clientSearch, setClientSearch] = useState('');

  const { data: clientsResponse } = useClients({
    search: clientSearch,
    status: UserStatus.Active,
  });
  
  const payerClients = clientsResponse?.data || [];
  const sameAsHolder = form.watch('invoiceSameAsHolder');

  const selectedClientId = form.watch('userData.paidByUserId');
  const { data: selectedClientData } = useClient(selectedClientId || '');

  const titularLegalName = form.watch('userData.legalName');
  const titularDocType = form.watch('userData.docType');
  const titularDocNumber = form.watch('userData.docNumber');

  useEffect(() => {
    if (sameAsHolder) {
      form.setValue('userData.invoiceLegalName', titularLegalName || '', { shouldValidate: false });
      form.setValue('userData.invoiceDocType', titularDocType || DocType.DNI, { shouldValidate: false });
      form.setValue('userData.invoiceDocNumber', titularDocNumber || '', { shouldValidate: false });
      form.setValue('userData.invoiceAddress', form.getValues('userData.street') || '', { shouldValidate: false });
      form.setValue('userData.ivaCondition', IvaCondition.CONSUMIDOR_FINAL, { shouldValidate: false });
    } else {
      form.setValue('userData.invoiceLegalName', '', { shouldValidate: false });
      form.setValue('userData.invoiceDocType', DocType.DNI, { shouldValidate: false });
      form.setValue('userData.invoiceDocNumber', '', { shouldValidate: false });
      form.setValue('userData.invoiceAddress', '', { shouldValidate: false });
      form.setValue('userData.ivaCondition', IvaCondition.CONSUMIDOR_FINAL, { shouldValidate: false });
    }
  }, [titularLegalName, titularDocType, titularDocNumber, sameAsHolder, form]);

  useEffect(() => {
    if (selectedClientData?.userData && !sameAsHolder) {
      const invData = selectedClientData.userData;
      form.setValue('userData.invoiceLegalName', invData.legalName || '', { shouldValidate: false });
      form.setValue('userData.invoiceDocType', invData.docType || DocType.DNI, { shouldValidate: false });
      form.setValue('userData.invoiceDocNumber', invData.docNumber || '', { shouldValidate: false });
      form.setValue('userData.invoiceAddress', invData.address || '', { shouldValidate: false });
      form.setValue('userData.ivaCondition', invData.ivaCondition || IvaCondition.CONSUMIDOR_FINAL, { shouldValidate: false });
    }
  }, [selectedClientData, sameAsHolder, form]);

  return (
    <>
      {/* Checkbox: same as holder */}
      <FormField
        control={form.control}
        name="invoiceSameAsHolder"
        render={({ field }) => (
          <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4 shadow-sm md:col-span-2">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel>
                Los datos de facturación son los mismos que los datos del titular
              </FormLabel>
              <p className="text-sm text-muted-foreground mt-1">
                Desmarque esta opción si los datos de facturación difieren del titular
              </p>
            </div>
          </FormItem>
        )}
      />

      {/* Row 1: Name + Address */}
      <FormField
        control={form.control}
        name="userData.invoiceLegalName"
        render={({ field }) => (
          <FormItem className="md:col-span-1">
            <FormLabel>{isPF ? 'Razón Social' : 'Apellido, Nombre'}</FormLabel>
            <FormControl>
              <Input
                readOnly
                className="bg-muted/50 text-muted-foreground"
                placeholder={isPF ? 'Empresa Ejemplo SA' : 'Pérez, Juan'}
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="userData.invoiceAddress"
        render={({ field }) => (
          <FormItem className="md:col-span-1">
            <FormLabel>Dirección de Facturación</FormLabel>
            <FormControl>
              <Input
                readOnly
                className="bg-muted/50 text-muted-foreground"
                placeholder="Av. Example 123"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Row 2: 3 columns - Doc Type, Doc Number, IVA Condition */}
      <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-6">
        <FormField
          control={form.control}
          name="userData.invoiceDocType"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Tipo de Documento</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value} disabled={sameAsHolder}>
                <FormControl>
                  <SelectTrigger className="bg-muted/50 text-muted-foreground">
                    <SelectValue placeholder="Seleccionar tipo de documento" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {getDocTypeOptions().map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="userData.invoiceDocNumber"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Número de Documento</FormLabel>
              <FormControl>
                <Input readOnly className="bg-muted/50 text-muted-foreground" placeholder="12345678" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="userData.ivaCondition"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Condición de IVA</FormLabel>
              <SelectField
                value={field.value}
                onValueChange={field.onChange}
                placeholder="Seleccionar condición IVA"
                triggerRef={field.ref}
                disabled={!!selectedClientId}
              >
                <SelectItem value="consumidor_final">Consumidor Final</SelectItem>
                <SelectItem value="responsable_inscripto">Responsable Inscripto</SelectItem>
                <SelectItem value="responsable_no_inscripto">Responsable No Inscripto</SelectItem>
                <SelectItem value="responsable_monotributo">Responsable Monotributo</SelectItem>
                <SelectItem value="iva_no_responsable">IVA No Responsable</SelectItem>
                <SelectItem value="iva_exento">Iva Exento</SelectItem>
              </SelectField>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {/* Only shown when unchecked (data differs from holder) */}
      {!sameAsHolder && (
        <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6 p-4 border rounded-md bg-muted/20">
          <FormField
            control={form.control}
            name="userData.paidByUserId"
            render={({ field }) => {
              const selectedClientItems = selectedClientId && selectedClientData
                ? [{
                  id: selectedClientId,
                  label: `${selectedClientData.userData.legalName || selectedClientData.fullName} - N° ${selectedClientData.userData.code}`
                }]
                : [];
              const searchResults = payerClients
                .filter((clientItem) => !!clientItem.id && !selectedClientItems.some(i => i.id === clientItem.id))
                .map((clientItem) => ({
                  id: clientItem.id,
                  label: `${clientItem.name} - N° ${clientItem.code}`
                }));

              return (
                <FormItem className="md:col-span-2">
                  <FormLabel>Datos de facturación de otro cliente</FormLabel>
                  <SelectSearch
                    value={field.value!}
                    onValueChange={(value) => {
                      field.onChange(value);
                    }}
                    onSearchChange={setClientSearch}
                    placeholder="Seleccionar cliente"
                    searchPlaceholder="Buscar cliente..."
                    emptyMessage="No se encontraron clientes."
                    items={[...selectedClientItems, ...searchResults]}
                  />
                  <FormMessage />
                </FormItem>
              );
            }}
          />
        </div>
      )}
    </>
  );
};
